// src/pages/CheckInCompletePage.tsx
import React, { useContext, useState } from "react";
import { Box, Button, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Dayjs } from "dayjs";

// MUI X Date Pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

// App imports
import PrevGuestIcon from "../assets/icons/guest_det_prev_guest.svg";
import PhysicalKey from "../components/PhysicalKey";
import ContentContainer from "./ContentContainer";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { AppContext } from "../context/AppContext";
import useProtectedStep from "../hooks/useProtectedStep";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { toast } from "react-hot-toast";
import { modifyBookingEta } from "../api/ReservationService";
import { isModifyBookingEnabled } from "../config/tenant";
import axios from "axios";
const CheckInCompletePage: React.FC = () => {
	useProtectedStep();
	const { navigateWithLang } = useNavigateWithLang();
	const { setCurrentStep, state } = useContext(AppContext) ?? {};

	const { colors: BrandColors } = useBrandAppearance();
	const { t } = useTranslation("check_in_complete");
	const { t: navBtn } = useTranslation("guests_navigation_buttons");
	const theme = useTheme();

	/** From reservation login (`hotelName`); never hardcode a specific property here */
	const hotelDisplayName =
		(state?.hotel && String(state.hotel).trim()) ||
		t("subheaderHotelFallback", { defaultValue: "your hotel" });
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// ETA state – keep string for API, Dayjs for the picker
	const [eta, setEta] = useState<string>("");
	const [etaValue, setEtaValue] = useState<Dayjs | null>(null);

	// Track last successfully saved ETA (HH:mm). If it matches `eta`, hide Save.
	const [lastSavedEta, setLastSavedEta] = useState<string | null>(null);

	const [saving, setSaving] = useState(false);

	const handlePrevGuest = () => {
		setCurrentStep?.(1);
		navigateWithLang(`/guest-info`);
	};

	const handleSaveEta = async () => {
		if (!eta) return;

		const confirmationNumber = resolveConfirmationNumber();

		const hotelId =
			(state as any)?.hotel?.id ||
			(state as any)?.hotelId ||
			(state as any)?.hotel?.code ||
			"";

		if (!confirmationNumber) {
			toast.error(t("etaMissingConfirmation", { defaultValue: "Missing confirmation number." }));
			return;
		}
		if (!hotelId) {
			toast.error(t("etaMissingHotel", { defaultValue: "Missing hotelId." }));
			return;
		}

		try {
			setSaving(true);
			await modifyBookingEta(confirmationNumber, hotelId, eta);
			// success → remember the saved value so the button hides
			setLastSavedEta(eta);
			toast.success(t("etaSaved", { defaultValue: "Arrival time saved." }));
		} catch (e) {
			console.error(e);
			toast.error(etaErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const showSaveButton = !!eta && eta !== lastSavedEta;
	const modifyBookingEnabled = isModifyBookingEnabled();

	const resolveConfirmationNumber = (): string =>
		sessionStorage.getItem("confirmationNumber") ||
		(state as any)?.confirmationNumber ||
		"";

	const etaErrorMessage = (err: unknown): string => {
		if (axios.isAxiosError(err)) {
			const data = err.response?.data as { errorCode?: string; message?: string } | undefined;
			if (data?.errorCode) {
				return t("etaSaveErrorWithCode", {
					defaultValue: "Couldn't save arrival time ({{code}}).",
					code: data.errorCode,
				});
			}
			if (data?.message) {
				return data.message;
			}
		}
		return t("etaSaveError", { defaultValue: "Couldn't save arrival time." });
	};

	return (
		<ContentContainer
			leftContainerIcon={
				<img
					src={PrevGuestIcon}
					alt="Previous Guest Icon"
					width={48}
					height={48}
				/>
			}
			leftContainerText={navBtn("view_guests")}
			onLeftContainerClick={handlePrevGuest}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					maxWidth: "1200px",
					margin: "0 auto",
				}}
			>
				<Container
					maxWidth="lg"
					sx={{
						mt: 2,
						textAlign: "left",
						display: "flex",
						position: "relative",
					}}
				/>

				<Container maxWidth="lg">
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-start",
							alignItems: "center",
							textAlign: "center",
							paddingX: { xs: 2, md: 14 },
						}}
					>
						<Typography
							variant="h4"
							sx={{
								fontFamily: "'Dala Floda', serif",
								fontSize: "2.5rem",
								fontWeight: "bold",
								mb: { xs: 0, md: 4 },
							}}
						>
							{t("title")}
						</Typography>

						<Box
							sx={{
								marginTop: { xs: "1rem", md: "1.9rem" },
								textAlign: "center",
							}}
						>
							<Typography sx={{ fontSize: "1.3rem", color: "#000" }}>
								{t("subheaderIntro")}
							</Typography>
							<Typography
								sx={{
									fontSize: "1.3rem",
									color: "#000",
									fontWeight: 600,
									mt: 1,
								}}
							>
								{hotelDisplayName}
							</Typography>
						</Box>

						{/* Key image */}
						<PhysicalKey />

						{modifyBookingEnabled ? (
						<>
							{/* Optional ETA (arrival time) */}
							<Box
									sx={{
										mt: 5,
										width: "100%",
										display: "flex",
										flexDirection: { xs: "column", sm: "row" },
										gap: 2,
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<TimePicker
											value={etaValue}
											onChange={(v) => {
												setEtaValue(v);
												setEta(v ? v.format("HH:mm") : "");
											}}
											ampm={false}
											views={["hours", "minutes"]}
											openTo="hours"
											slotProps={{
												textField: {
													variant: "outlined",
													label: t("etaLabel", {
														defaultValue:
															"Your estimated time of arrival (optional)",
													}),
													InputLabelProps: { shrink: true },
													sx: {
														minWidth: 260,
														"& .MuiOutlinedInput-root": {
															borderRadius: 2,
															backgroundColor: "#FFF",
															"&:hover .MuiOutlinedInput-notchedOutline": {
																borderColor: "#7C624B",
															},
															"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
																borderColor: "#7C624B",
																borderWidth: "2px",
															},
														},
														"& .MuiInputLabel-root": {
															fontSize: "0.9rem",
															color: "#999",
															"&.Mui-focused": { color: "#7C624B" },
														},
													},
												},

												// For single-column digital clock (some configs)
												digitalClockItem: {
													sx: {
														borderRadius: 2,
														"&.Mui-selected": {
															backgroundColor: "#7C624B",
															color: "#fff",
														},
														"&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible":
														{
															backgroundColor: "#7C624B",
														},
													},
												},

												// Style the two-column (hours/minutes) clock via selectors
												desktopPaper: {
													sx: {
														borderRadius: 3,
														boxShadow: "0 16px 40px rgba(0,0,0,.18)",
														"& .MuiMultiSectionDigitalClockSection-root": {
															width: 100,
														}, // wider columns
														"& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected":
														{
															backgroundColor: `${BrandColors.primary} !important`,
															color: "#fff",
														},
														"& .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected:hover, & .MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item.Mui-selected.Mui-focusVisible":
														{
															backgroundColor: `${BrandColors.primary} !important`,
														},
														"& .MuiMenuList-root": { pr: 2 }, // room for scrollbar
														"& .MuiMenuItem-root": {
															// every row
															height: 44,
															mx: 1,
															my: 0.25,
															borderRadius: 2,
														},
														"& .MuiMenuItem-root.Mui-selected": {
															backgroundColor: "#7C624B",
															color: "#fff",
														},
														"& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root.Mui-selected.Mui-focusVisible":
														{
															backgroundColor: "#7C624B",
														},
													},
												},
												mobilePaper: {
													sx: {
														borderRadius: 3,
														"& .MuiMultiSectionDigitalClockSection-root": {
															width: 120,
														},
														"& .MuiMenuList-root": { pr: 1.5 },
														"& .MuiMenuItem-root": {
															height: 44,
															mx: 1,
															my: 0.25,
															borderRadius: 2,
														},
														"& .MuiMenuItem-root.Mui-selected": {
															backgroundColor: "#7C624B",
															color: "#fff",
														},
														"& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root.Mui-selected.Mui-focusVisible":
														{
															backgroundColor: "#7C624B",
														},
													},
												},

												// Show OK/Cancel on mobile only
												actionBar: {
													actions: isMobile ? ["accept", "cancel"] : [],
												},
											}}
										/>
									</LocalizationProvider>

							{showSaveButton ? (
								<Button
									variant="contained"
									onClick={handleSaveEta}
									disabled={saving}
									sx={{
										borderRadius: 0,
										backgroundColor: BrandColors.accent,
										color: "#FFFFFF",
										px: 3,
										py: 1.25,
										"&:hover": { backgroundColor: BrandColors.accent },
									}}
								>
									{saving
										? t("savingEta", { defaultValue: "Saving..." })
										: t("saveEtaButton", { defaultValue: "Save" })}
								</Button>
							) : null}
						</Box>
					</>
						) : null}
					</Box>
				</Container>
			</Box>
		</ContentContainer>
	);
};

export default CheckInCompletePage;
